import { ALERT_TYPE_MAPPING } from "../General/alertTypes";
import { safeNumber } from "../General/timeFormatter";

export const groupDataByField = (data, groupField, alertTypes = null) => {
  const grouped = {};

  data.forEach((row) => {
    const groupKey = row[groupField];
    if (!grouped[groupKey]) {
      grouped[groupKey] = {
        [groupField === "alert_id" ? "issue" : groupField]:
          groupField === "alert_id"
            ? ALERT_TYPE_MAPPING[groupKey] || `Alert ${groupKey}`
            : groupKey,
        alerts: 0,
        waterWaste: 0,
        chemicalWaste: 0,
        timeWaste: 0,
      };

      // For chart data, initialize all alert types
      if (alertTypes) {
        alertTypes.forEach((type) => {
          grouped[groupKey][type] = 0;
        });
      }
    }

    grouped[groupKey].alerts += 1;
    grouped[groupKey].waterWaste += row.water_waste_l || 0;
    grouped[groupKey].chemicalWaste += row.chemical_waste_l || 0;
    grouped[groupKey].timeWaste += row.time_waste_s || 0;
  });

  return Object.values(grouped).sort((a, b) => b.alerts - a.alerts);
};

export const createGlobalRanges = (filteredData) => {
  // Collect all possible values that could appear in any table
  const allAlerts = [];
  const allWaterWaste = [];
  const allChemicalWaste = [];
  const allTimeWaste = [];

  // From raw data
  filteredData.forEach((row) => {
    allAlerts.push(1); // Each row represents 1 alert occurrence
    allWaterWaste.push(row.water_waste_l || 0);
    allChemicalWaste.push(row.chemical_waste_l || 0);
    allTimeWaste.push((row.time_waste_s || 0) / 60); // Convert to minutes
  });

  // Add some padding to ranges
  const alertsMax = Math.max(...allAlerts, 1);
  const waterMax = Math.max(...allWaterWaste, 1);
  const chemicalMax = Math.max(...allChemicalWaste, 1);
  const timeMax = Math.max(...allTimeWaste, 1);

  return {
    alerts: { min: 0, max: alertsMax },
    waterWaste: { min: 0, max: waterMax },
    chemicalWaste: { min: 0, max: chemicalMax },
    timeWaste: { min: 0, max: timeMax },
  };
};

export const generateChartData = (
  data,
  viewMode,
  selectedAlertTypes,
  selectedMetric,
  convertVolume,
  processedTotals = null,
  showAbsolute = true,
  cipTotalsData = []
) => {
  if (selectedMetric === "cip_rate") {
    return generateCipDefectRateData(
      data,
      viewMode,
      selectedAlertTypes,
      processedTotals,
      showAbsolute,
      cipTotalsData
    );
  }

  const groupedData = {};
  const fieldMap = {
    object: "object_name",
    line: "line",
    recipe: "recipe_name",
  };

  data.forEach((row) => {
    const groupKey = row[fieldMap[viewMode]];
    const alertType = row.alert_id;

    if (!groupedData[groupKey]) {
      groupedData[groupKey] = { name: groupKey };
      selectedAlertTypes.forEach((type) => {
        groupedData[groupKey][type] = 0;
      });
    }

    let value;
    if (selectedMetric === "alerts") {
      value = 1;
    } else if (selectedMetric === "time_waste_s") {
      value = safeNumber(row.time_waste_s, 0);
    } else if (
      selectedMetric === "water_waste_l" ||
      selectedMetric === "chemical_waste_l"
    ) {
      const rawValue = safeNumber(row[selectedMetric], 0);
      value = safeNumber(convertVolume(rawValue), 0);
    } else {
      value = safeNumber(row[selectedMetric], 0);
    }

    groupedData[groupKey][alertType] += value;
  });

  return Object.values(groupedData).sort((a, b) =>
    a.name.localeCompare(b.name, undefined, {
      numeric: true,
      sensitivity: "base",
    })
  );
};

export const generateCipDefectRateData = (
  data,
  viewMode,
  selectedAlertTypes,
  processedTotals,
  showAbsolute = true,
  cipTotalsData = [] // Add this parameter
) => {
  const groupedData = {};
  const fieldMap = {
    object: "object_name",
    line: "line",
    recipe: "recipe_name",
  };

  // Group CIPs by alert type for each group (object/line/recipe)
  data.forEach((row) => {
    const groupKey = row[fieldMap[viewMode]];
    const alertType = row.alert_id;

    if (!groupedData[groupKey]) {
      groupedData[groupKey] = {
        name: groupKey,
        alertTypes: {},
        allDefectedCips: new Set(),
      };
    }

    // Initialize alert type set if needed
    if (!groupedData[groupKey].alertTypes[alertType]) {
      groupedData[groupKey].alertTypes[alertType] = new Set();
    }

    // Add CIP to this alert type (Set prevents duplicates)
    groupedData[groupKey].alertTypes[alertType].add(row.cip_object_id);
    // Also add to overall defected CIPs set
    groupedData[groupKey].allDefectedCips.add(row.cip_object_id);
  });

  // Convert to chart data format
  return Object.entries(groupedData)
    .map(([groupKey, data]) => {
      // Get total CIPs for this group using UNFILTERED totals
      let totalCips = 0;

      if (cipTotalsData && cipTotalsData.length > 0) {
        // Use unfiltered totals for accurate percentage
        if (viewMode === "object") {
          totalCips = cipTotalsData.filter(
            (item) =>
              item.objectName === groupKey || item.object_name === groupKey
          ).length;
        } else if (viewMode === "line") {
          totalCips = cipTotalsData.filter(
            (item) => item.line === groupKey
          ).length;
        } else if (viewMode === "recipe") {
          totalCips = cipTotalsData.filter(
            (item) =>
              item.recipeName === groupKey || item.recipe_name === groupKey
          ).length;
        }
      } else {
        // Fallback to processed totals if cipTotalsData not available
        if (viewMode === "object") {
          totalCips = processedTotals?.byObject?.[groupKey] || 0;
        } else if (viewMode === "line") {
          totalCips = processedTotals?.byLine?.[groupKey] || 0;
        } else if (viewMode === "recipe") {
          totalCips = processedTotals?.byRecipe?.[groupKey] || 0;
        }
      }

      const result = { name: groupKey, totalCips };

      // For each selected alert type, calculate value based on showAbsolute
      selectedAlertTypes.forEach((alertType) => {
        const cipsWithThisAlert = data.alertTypes[alertType]?.size || 0;

        if (showAbsolute) {
          result[alertType] = cipsWithThisAlert; // Absolute count
        } else {
          result[alertType] =
            totalCips > 0 ? (cipsWithThisAlert / totalCips) * 100 : 0; // Percentage
        }
      });

      return result;
    })
    .sort((a, b) =>
      a.name.localeCompare(b.name, undefined, {
        numeric: true,
        sensitivity: "base",
      })
    );
};
